import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import Spinner from '@dashboard/components/Spinner';
import Button from '@dashboard/components/Button';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import DialogOld from '@dashboard/components/DialogOld';
import Link from '@dashboard/components/Link';
import { Alert, AlertTitle } from '@dashboard/components/ui/alert';
import ConnectStripeDialog from '@dashboard/components/ConnectStripeDialog';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import { pageKey, pagesKey } from '@dashboard/lib/queryKeys';
import StripeLogo from './stripe.png';
import getShouldSyncSubscription from './getShouldSyncSubscription';
import CheckoutShareDirect from './scenes/CheckoutShareDirect';
import ShareActionCards from './scenes/ShareActionCards';
import AddToWebsiteOverlay from './scenes/AddToWebsiteOverlay';
import QRCodeOverlay from './scenes/QRCodeOverlay';

const TITLE_MAP = {
  event: 'Share event',
  checkout: 'Share checkout page',
  form: 'Share form',
};

const ARCHIVED_NOUN = {
  event: 'event',
  checkout: 'checkout page',
  form: 'form',
};

const CheckoutShareDialog = ({ checkoutId, isOpen = false, onClose }) => {
  return (
    <DialogOld isOpen={isOpen} onClose={onClose} size="3xl">
      <CheckoutShareDialogContent checkoutId={checkoutId} onClose={onClose} />
    </DialogOld>
  );
};

function CheckoutShareDialogContent({ checkoutId, onClose }) {
  const { token, seller, sellerId } = useAuth();
  const [connectWithStripeDialogOpen, setConnectWithStripeDialogOpen] = useState(false);
  const [syncSubscriptionDialogOpen, setSyncSubscriptionDialogOpen] = useState(false);
  const [addToWebsiteOpen, setAddToWebsiteOpen] = useState(false);
  const [qrCodeOpen, setQrCodeOpen] = useState(false);

  const {
    isLoading,
    isError,
    data: page,
  } = useQuery({
    queryKey: pageKey(checkoutId),
    queryFn: () => fetcher(`/sellers/${sellerId}/pages/${checkoutId}`, token),
    enabled: !!token,
  });

  useEffect(() => {
    if (isEmpty(page?.product)) return;
    if (isEmpty(seller)) return;

    if (getShouldSyncSubscription(page, seller)) {
      setSyncSubscriptionDialogOpen(true);
    }
  }, [page?.product?.type, page?.product?.planActive]);

  if (isLoading) {
    return (
      <div className="p-4">
        <Spinner />
      </div>
    );
  }

  if (!isLoading && isError) {
    return <div className="p-4">Something went wrong</div>;
  }

  const shouldSyncSubscription = page?.product && getShouldSyncSubscription(page, seller);
  const title = TITLE_MAP[page?.type] || 'Share checkout page';

  if (addToWebsiteOpen) {
    return (
      <AddToWebsiteOverlay page={page} seller={seller} onBack={() => setAddToWebsiteOpen(false)} />
    );
  }

  if (qrCodeOpen) {
    return <QRCodeOverlay page={page} seller={seller} onBack={() => setQrCodeOpen(false)} />;
  }

  return (
    <div className="text-left h-full flex-1 flex flex-col">
      <ConnectStripeDialog
        onClose={() => setConnectWithStripeDialogOpen(false)}
        open={connectWithStripeDialogOpen}
      />

      {syncSubscriptionDialogOpen && (
        <SyncSubscriptionDialog
          onClose={() => setSyncSubscriptionDialogOpen(false)}
          product={page.product}
        />
      )}

      {!syncSubscriptionDialogOpen && (
        <div className="flex flex-col flex-1">
          <h2 className="mb-6 text-lg font-bold">{title}</h2>

          {page.status === 'archived' && (
            <Alert variant="warning" className="mb-6">
              <ExclamationCircleIcon />
              <AlertTitle>
                This {ARCHIVED_NOUN[page.type] ?? 'checkout page'} is archived
              </AlertTitle>
            </Alert>
          )}

          {!checkIsConnectedToStripe(seller) && page.type !== 'form' && (
            <ConnectWithStripeBanner onClick={() => setConnectWithStripeDialogOpen(true)} />
          )}

          {shouldSyncSubscription && (
            <SyncSubscriptionBanner onClick={() => setSyncSubscriptionDialogOpen(true)} />
          )}

          <CheckoutShareDirect page={page} seller={seller} closeDialog={onClose} />

          <ShareActionCards
            page={page}
            onAddToWebsite={() => setAddToWebsiteOpen(true)}
            onQRCode={() => setQrCodeOpen(true)}
          />
        </div>
      )}
    </div>
  );
}

function ConnectWithStripeBanner({ onClick }) {
  return (
    <div
      className="p-2 rounded border border-foreground bg-muted flex items-center gap-x-2 mt-4 mb-4 hover:bg-accent cursor-pointer transition-colors"
      onClick={onClick}
    >
      <ExclamationCircleIcon className="h-6 w-6 text-foreground" />
      <span>Connect with Stripe to process payments</span>
      <div className="font-medium text-foreground ml-auto px-2 flex-1 whitespace-nowrap text-right">
        Connect
      </div>
    </div>
  );
}

function SyncSubscriptionBanner({ onClick }) {
  return (
    <div
      className="p-2 rounded border border-foreground bg-muted flex items-center gap-x-2 mt-4 mb-8 hover:bg-accent cursor-pointer transition-colors"
      onClick={onClick}
    >
      <ExclamationCircleIcon className="h-6 w-6 text-foreground" />
      <span>Create your subscription in Stripe to process payments</span>
      <div className="font-medium text-foreground ml-auto px-2 flex-1 whitespace-nowrap text-right">
        Set up
      </div>
    </div>
  );
}

function SyncSubscriptionDialog({ onClose, product = {} }) {
  const queryClient = useQueryClient();
  const [syncSubscriptionState, setSyncSubscriptionState] = useState('initial'); // initial, syncing, synced, error
  const [createStripePriceError, setCreateStripePriceError] = useState('');
  const { token } = useAuth();

  const createStripePriceMutation = useMutation({
    mutationFn: async () => {
      setSyncSubscriptionState('syncing');

      return fetcher(
        `/sellers/${product.seller}/products/${product._id}/create-stripe-price`,
        token,
        {
          method: 'POST',
        },
      );
    },
    onSuccess: () => {
      setSyncSubscriptionState('synced');
      queryClient.invalidateQueries({ queryKey: pagesKey });
      window.setTimeout(() => {
        onClose();
        setSyncSubscriptionState('initial');
      }, 2000);
    },
    onError: () => {
      setCreateStripePriceError(
        'Failed to create subscription in Stripe. Please try again or contact support.',
      );
      setSyncSubscriptionState('error');
    },
  });

  return (
    <div className="text-center">
      <div className="flex justify-center mt-6 mb-4">
        <img className="h-20" src={StripeLogo} alt="Stripe" />
      </div>

      <h2 className="text-xl font-bold mb-2">Create subscription in Stripe</h2>

      <p className="text-gray-800 dark:text-foreground text-lg mb-8 max-w-lg mx-auto">
        Before sharing this checkout, we need to create this subscription in your Stripe account.
      </p>

      <div className="space-y-2 mb-4">
        <Button
          type="button"
          onClick={() => createStripePriceMutation.mutate()}
          appearance="primary"
          className="text-lg py-3 px-12"
          disabled={syncSubscriptionState === 'syncing' || syncSubscriptionState === 'synced'}
        >
          {(syncSubscriptionState === 'initial' || syncSubscriptionState === 'error') &&
            'Create in Stripe'}

          {syncSubscriptionState === 'syncing' && (
            <>
              Creating in Stripe <Spinner className="ml-2 h-5 w-5" appearance="light" />
            </>
          )}

          {syncSubscriptionState === 'synced' && (
            <>
              Subscription created <CheckCircleIcon className="ml-2 h-5 w-5 text-white" />
            </>
          )}
        </Button>

        <FormErrorBase>{createStripePriceError}</FormErrorBase>
      </div>

      <button onClick={onClose}>
        <Link variant="secondary">Do this later</Link>
      </button>
    </div>
  );
}

export default CheckoutShareDialog;
